import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui';
import { AuthProvider, useAuth } from './lib/auth';
import { TenantProvider, useTenant } from './lib/tenant';
import { isNativeApp } from './lib/app';
import { Confirm } from './pages/Confirm';
import { Login } from './pages/Login';
import { Setup } from './pages/Setup';
import { QrApprove } from './pages/QrApprove';
import { SchoolWelcome } from './pages/SchoolWelcome';
import { SchoolUnavailable } from './pages/SchoolUnavailable';

const Landing = lazy(() => import('./pages/landing/Landing'));
const DemoStart = lazy(() => import('./pages/landing/DemoStart'));
const PlatformApp = lazy(() => import('./pages/platform/PlatformApp'));

const Gate = lazy(() => import('./pages/staff/Gate'));
const Board = lazy(() => import('./pages/staff/Board'));
const Students = lazy(() => import('./pages/admin/Students'));
const StudentDetail = lazy(() => import('./pages/admin/StudentDetail'));
const Users = lazy(() => import('./pages/admin/Users'));
const Messages = lazy(() => import('./pages/admin/Messages'));
const Reports = lazy(() => import('./pages/admin/Reports'));
const Audit = lazy(() => import('./pages/admin/Audit'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const Today = lazy(() => import('./pages/guardian/Today'));
const People = lazy(() => import('./pages/guardian/People'));
const Changes = lazy(() => import('./pages/guardian/Changes'));
const History = lazy(() => import('./pages/guardian/History'));
const Account = lazy(() => import('./pages/Account'));

function Routed() {
  const { user, ready } = useAuth();
  const tenant = useTenant();
  const { t } = useTranslation();
  const loading = <div className="splash">{t('common.loading')}</div>;
  if (tenant.kind === 'unknown') return <SchoolUnavailable reason="not_found" />;
  if (!ready) return loading;
  const onPlatform = tenant.kind === 'platform';
  // Inside the iPhone/Android app the main address is only for signing in: no landing page, prices or sales forms
  const inApp = isNativeApp();
  if (!user)
    return (
      <Suspense fallback={loading}>
        <Routes>
          <Route path="/c/:token" element={<Confirm />} />
          <Route path="/setup/:token" element={<Setup />} />
          {/* Scanned a sign-in QR but not signed in on this phone yet: sign in first, then approve */}
          <Route path="/qr/:code" element={<Login note={t('auth.qrSignInFirst')} />} />
          {onPlatform && <Route path="/platform/*" element={<PlatformApp />} />}
          {onPlatform && tenant.demoEnabled && <Route path="/demo" element={<DemoStart />} />}
          {onPlatform && <Route path="/" element={inApp ? <Navigate to="/login" replace /> : <Landing />} />}
          {onPlatform && <Route path="/welcome" element={inApp ? <Navigate to="/login" replace /> : <Landing />} />}
          {tenant.kind === 'school' && tenant.school?.status !== 'suspended' && <Route path="/" element={<SchoolWelcome />} />}
          {tenant.school?.status === 'suspended' ? <Route path="*" element={<SchoolUnavailable reason="suspended" />} /> : <Route path="*" element={<Login />} />}
        </Routes>
      </Suspense>
    );
  const isStaff = user.role !== 'guardian';
  const isAdmin = user.role === 'admin';
  return (
    <Suspense fallback={<div className="splash">{t('common.loading')}</div>}>
      <Routes>
        <Route path="/c/:token" element={<Confirm />} />
        <Route path="/setup/:token" element={<Setup />} />
        <Route path="/qr/:code" element={<QrApprove />} />
        {onPlatform && <Route path="/platform/*" element={<PlatformApp />} />}
        {onPlatform && tenant.demoEnabled && <Route path="/demo" element={<DemoStart />} />}
        {onPlatform && <Route path="/welcome" element={inApp ? <Navigate to="/" replace /> : <Landing />} />}
        {/* Full screen, without the top bar: a tablet with a barcode scanner at the gate */}
        {isStaff && <Route path="/gate" element={<Gate />} />}
        <Route element={<Layout />}>
          {isStaff ? (
            <>
              <Route index element={<Board />} />
              <Route path="students" element={<Students />} />
              <Route path="students/:id" element={<StudentDetail />} />
              <Route path="messages" element={<Messages />} />
              <Route path="reports" element={<Reports />} />
              {isAdmin && (
                <>
                  <Route path="users" element={<Users />} />
                  <Route path="audit" element={<Audit />} />
                  <Route path="settings" element={<Settings />} />
                </>
              )}
            </>
          ) : (
            <>
              <Route index element={<Today />} />
              <Route path="people" element={<People />} />
              <Route path="changes" element={<Changes />} />
              <Route path="history" element={<History />} />
            </>
          )}
          <Route path="account" element={<Account />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export function App() {
  return (
    <TenantProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routed />
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </TenantProvider>
  );
}
